import { FC, ReactNode } from 'react';
import { FormikProvider, useFormik } from 'formik';
import ControlledForm, { OnChange, Values } from './ControlledForm';

export interface Props {
    children?: ReactNode;
    initialValues ?: object;
    validationSchema ?: object;
    onSubmit ?: (values: Values) => any;
    [x:string]: any;
}

export const ControlledFormFormik: FC<Props> = (props) => {
    const { children, initialValues, validationSchema, onSubmit, ...other } = props;
    const formik = useFormik({
        initialValues: initialValues || {},
        validationSchema: validationSchema,
        onSubmit: onSubmit || (_value => null),
        validateOnMount: true
    });
    const touched = formik.touched as Record<string, boolean>;

    const handleChange: OnChange = (name, value) => {
        formik.setFieldValue(name, value);
        formik.setTouched({ ...touched, [name]: true });
    }

    return <FormikProvider value={formik}>
        <ControlledForm
            {...other}
            children={typeof children === 'function' ? children(formik) : children}
            onChange={handleChange}
            data={formik.values}
            errors={formik.errors}
            onSubmit={formik.handleSubmit}
        />
    </FormikProvider>
}

// WARNING: be careful with very deeply nested objects since js interpreter might not support tail call optimization
const flattenNestedValues = (value: any, path?: string): any => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        let newObject: Record<string, any> = {};
        Object.entries(value).forEach(([key, value]) => {
            const newPath = path ? `${path}.${key}` : key;
            newObject = { ...newObject, ...flattenNestedValues(value, newPath)};
        });
        return newObject;
    }

    if (Array.isArray(value)) {
        return value.reduce((acc, v, idx) => {
            const newPath = `${path || ''}[${idx}]`;
            return { ...acc, ...flattenNestedValues(v, newPath) };
        }, {});
    }

    if (!path) return value;
    return { [path]: value };
}

export default ControlledFormFormik;
